import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronDownIcon, SearchIcon, XIcon } from "lucide-react";
import { ProductCard } from "../components/ProductCard";
import { products } from "../data/products";
import { collections } from "../data/collections";
import { needFilters } from "../data/store";
type SortKey = "featured" | "price-asc" | "price-desc" | "name";
const sortOptions: {
  value: SortKey;
  label: string;
}[] = [{
  value: "featured",
  label: "Featured"
}, {
  value: "price-asc",
  label: "Price: low to high"
}, {
  value: "price-desc",
  label: "Price: high to low"
}, {
  value: "name",
  label: "Name A–Z"
}];
export function Shop() {
  const [params, setParams] = useSearchParams();
  const [sort, setSort] = useState<SortKey>("featured");
  const [query, setQuery] = useState("");
  const collection = params.get("collection");
  const need = params.get("for");
  const activeCollection = collections.find(c => c.slug === collection);
  const activeNeed = needFilters.find(n => n.value === need);
  const title = activeCollection?.name ?? activeNeed?.label ?? "Shop all";
  const subtitle = activeCollection?.description ?? "A range of premium products designed to elevate pleasure, passion and performance.";
  const setFilter = (key: "collection" | "for", value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null || next.get(key) === value) next.delete(key);else next.set(key, value);
    setParams(next);
  };
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = products.filter(p => (!collection || p.collection === collection) && (!need || p.category === need) && (!q || `${p.name} ${p.brand} ${p.tagline}`.toLowerCase().includes(q)));
    const sorted = [...list];
    if (sort === "price-asc") sorted.sort((a, b) => a.price - b.price);
    if (sort === "price-desc") sorted.sort((a, b) => b.price - a.price);
    if (sort === "name") sorted.sort((a, b) => a.name.localeCompare(b.name));
    return sorted;
  }, [collection, need, query, sort]);
  const hasFilters = Boolean(collection || need || query);
  const chip = (active: boolean) => `w-full whitespace-nowrap rounded-full border px-4 py-2 text-left text-sm font-medium transition-colors lg:rounded-lg lg:border-transparent lg:px-3 ${active ? "border-white bg-white text-night" : "border-line text-muted hover:text-white lg:hover:bg-raised"}`;
  return <div className="bg-night" data-id="element-446" data-mp="1wacyugiuq3cv">
      <section className="border-b border-line" data-id="element-447" data-mp="2ax67j2xv8aw3">
        <div className="mx-auto max-w-7xl px-6 py-14 lg:py-16" data-id="element-448" data-mp="2az0uemsp6d2t">
          <h1 className="font-display text-4xl font-extrabold tracking-tight sm:text-5xl" data-id="element-449" data-mp="2667s05kpbgtw">{title}</h1>
          <p className="mt-3 max-w-xl text-muted" data-id="element-450" data-mp="1igddljkqhk7j">{subtitle}</p>
        </div>
      </section>

      <div className="mx-auto grid max-w-7xl gap-10 px-6 py-10 lg:grid-cols-[220px_1fr] lg:py-14" data-id="element-451" data-mp="1dikaizeq96bh">
        <aside aria-label="Filters" className="space-y-8 lg:sticky lg:top-28 lg:self-start" data-id="element-452" data-mp="3p4vtopah1j7q">
          <div className="relative" data-id="element-453" data-mp="3twnmz7l0i31k">
            <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle" aria-hidden="true" data-id="element-454" data-mp="3txoz7fdrkvkv" />
            <label htmlFor="shop-search" className="sr-only" data-id="element-455" data-mp="3k6asonneavi8">Search products</label>
            <input id="shop-search" type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search products" className="w-full rounded-full border border-line bg-surface py-2.5 pl-10 pr-4 text-sm placeholder:text-subtle focus:border-white/40 focus:outline-none" data-id="element-456" data-mp="3k487qi13nskd" />
          </div>

          <div data-id="element-457" data-mp="7qqqukmb3w41">
            <h2 className="text-sm font-semibold" data-id="element-458" data-mp="2ng1xnfles7yp">Collection</h2>
            <div className="no-scrollbar -mx-6 mt-3 flex gap-2 overflow-x-auto px-6 lg:mx-0 lg:flex-col lg:gap-0.5 lg:px-0" data-id="element-459" data-mp="2neqrxa8ekq4y">
              <div className="shrink-0 lg:w-full" data-id="element-460" data-mp="37orw8f018ioz">
                <button type="button" onClick={() => setFilter("collection", null)} className={chip(!collection)} aria-pressed={!collection} data-id="element-461" data-mp="37pjsls1gekgb">
                  All collections
                </button>
              </div>
              {collections.map(c => <div key={c.slug} className="shrink-0 lg:w-full" data-id="element-462" data-mp="2txn21sffoim1">
                  <button type="button" onClick={() => setFilter("collection", c.slug)} className={chip(collection === c.slug)} aria-pressed={collection === c.slug} data-id="element-463" data-mp="2tvaft32n7m36">
                    {c.name}
                  </button>
                </div>)}
            </div>
          </div>

          <div data-id="element-464" data-mp="3cnl8y57hw5o3">
            <h2 className="text-sm font-semibold" data-id="element-465" data-mp="3cq7gi4udyl6b">Made for</h2>
            <div className="no-scrollbar -mx-6 mt-3 flex gap-2 overflow-x-auto px-6 lg:mx-0 lg:flex-col lg:gap-0.5 lg:px-0" data-id="element-466" data-mp="269dofdsdj15z">
              {needFilters.map(n => <div key={n.value} className="shrink-0 lg:w-full" data-id="element-467" data-mp="gr6yje6sr5l1">
                  <button type="button" onClick={() => setFilter("for", n.value)} className={chip(need === n.value)} aria-pressed={need === n.value} data-id="element-468" data-mp="goucopxx4b2p">
                    {n.label}
                  </button>
                </div>)}
            </div>
          </div>
        </aside>

        <div data-id="element-469" data-mp="zjj1a21d48vp">
          <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-470" data-mp="14g9fejtlwueh">
            <div className="flex flex-wrap items-center gap-3" data-id="element-471" data-mp="14icfjwoh43wx">
              <p className="text-sm text-muted" aria-live="polite" data-id="element-472" data-mp="19gnqybuf2mh9">
                {visible.length} {visible.length === 1 ? "product" : "products"}
              </p>
              {hasFilters && <button type="button" onClick={() => {
              setQuery("");
              setParams({});
            }} className="inline-flex items-center gap-1 rounded-full bg-raised px-3 py-1 text-xs font-medium hover:bg-line" data-id="element-473" data-mp="3b80scqx6ec8k">
                  <XIcon className="h-3 w-3" aria-hidden="true" data-id="element-474" data-mp="hgu42u3g5t1q" /> Clear filters
                </button>}
            </div>
            <label className="relative" data-id="element-475" data-mp="rd5k9v65r31g">
              <span className="sr-only" data-id="element-476" data-mp="rbc9q4sfbyo6">Sort by</span>
              <select value={sort} onChange={e => setSort(e.target.value as SortKey)} className="appearance-none rounded-full border border-line bg-surface py-2 pl-4 pr-10 text-sm font-medium focus:border-white/40 focus:outline-none" data-id="element-477" data-mp="15ceywqeptp3b">
                {sortOptions.map(o => <option key={o.value} value={o.value} data-id="element-478" data-mp="1a3x09fknhrot">
                    {o.label}
                  </option>)}
              </select>
              <ChevronDownIcon className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2" aria-hidden="true" data-id="element-479" data-mp="1j4cx466w20kp" />
            </label>
          </div>

          {visible.length === 0 ? <div className="py-24 text-center" data-id="element-480" data-mp="27phmy8scfizj">
              <p className="font-display text-xl font-semibold" data-id="element-481" data-mp="27o6jka2t8d8y">No products found</p>
              <p className="mt-2 text-muted" data-id="element-482" data-mp="1tonddmtk7bza">Try a different search or filter.</p>
              <button type="button" onClick={() => {
            setQuery("");
            setParams({});
          }} className="mt-6 rounded-full bg-brand px-6 py-3 text-sm font-bold text-night" data-id="element-483" data-mp="1xpqwci6wlo4q">
                Show all products
              </button>
            </div> : <div className="mt-8 grid grid-cols-2 gap-x-4 gap-y-12 sm:gap-x-5 md:grid-cols-3" data-id="element-484" data-mp="1f00z7ofo4q9n">
              {visible.map(product => <ProductCard key={product.slug} product={product} data-id="element-485" data-mp="111udt7rwoiqv" />)}
            </div>}
        </div>
      </div>
    </div>;
}